import type { TovDefinitionEvidenceResponse } from "contracts/api/client-tov/evidence.responses";
import type { TovSourceResponse } from "contracts/api/client-tov/sources.responses";
import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import { For, Show } from "solid-js";
import { relativeAge } from "../../ui/format";
import type {
  GuidelineCard,
  SourceDetail,
  SourceTrust,
} from "../tov/guidelineModel";
import { EmptyState, GuidelineCardGrid, SourceRow } from "./guidelineParts";
export function GuidanceTab(props: {
  title: string;
  description: string;
  emptyTitle: string;
  emptyText: string;
  cards: GuidelineCard[];
  onInspect: (item: GuidelineCard, link: TovDefinitionEvidenceResponse) => void;
}) {
  return (
    <section class="signal-panel signal-guideline-section">
      <div class="signal-panel-head">
        <div>
          <span class="signal-mini-kicker">Guidance</span>
          <h2>{props.title}</h2>
          <p>{props.description}</p>
        </div>
      </div>
      <GuidelineCardGrid
        cards={props.cards}
        emptyTitle={props.emptyTitle}
        emptyText={props.emptyText}
        onInspect={props.onInspect}
      />
    </section>
  );
}

export function SourcesTab(props: {
  sources: TovSourceResponse[];
  details: Map<string, SourceDetail>;
  evidenceBySource: Map<string, number>;
  trust: SourceTrust;
}) {
  return (
    <section class="signal-panel signal-guideline-section">
      <div class="signal-panel-head">
        <div>
          <span class="signal-mini-kicker">Sources</span>
          <h2>Source inspection</h2>
          <p>
            Uploaded or imported source documents used by the active version.
          </p>
        </div>
      </div>
      <Show
        when={props.sources.length > 0}
        fallback={
          <EmptyState
            title="No guideline sources added"
            text="No source documents are linked to this active version."
          />
        }
      >
        <div class="signal-source-table">
          <For each={props.sources}>
            {(source) => (
              <SourceRow
                source={source}
                detail={props.details.get(source.id)}
                evidenceCount={props.evidenceBySource.get(source.id) ?? 0}
                trust={props.trust}
              />
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

export function ActivityTab(props: {
  events: TovVersionResponse["revisionEvents"];
}) {
  return (
    <section class="signal-panel signal-guideline-section">
      <div class="signal-panel-head">
        <div>
          <span class="signal-mini-kicker">Activity</span>
          <h2>Version activity</h2>
          <p>
            Only revision events stored by the guideline workspace are shown.
          </p>
        </div>
      </div>
      <Show
        when={props.events.length > 0}
        fallback={
          <EmptyState
            title="No activity history available"
            text="This version has no stored revision events."
          />
        }
      >
        <ol class="signal-activity-list">
          <For each={props.events}>
            {(event) => (
              <li>
                <span>{event.actorType}</span>
                <div>
                  <strong>{event.summary}</strong>
                  <p>
                    {event.eventType} / {event.targetTable} /{" "}
                    {relativeAge(new Date(event.createdAt))}
                  </p>
                </div>
              </li>
            )}
          </For>
        </ol>
      </Show>
    </section>
  );
}
